import React, { useEffect, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAppDispatch } from '../../../../app/hooks';
import { tryToUploadPurchaseInvoiceFileCompare } from '../../../../store/brokers/partner/purchases/PurchasesSlice';
import Button from '../../../../shared/button';
import Modal from '../../../../shared/modal';
import Error from '../../../../shared/error';
import { CURRENCY_SYMBOLS } from '../../../../utils/constants/currency-symbols';


interface IUploadFilesModal {
    openModal: boolean;
    handleCloseModal: () => void;
    onSaveFilesSuccessfully: () => void;
}

const UploadFilesModal = ({
    openModal,
    handleCloseModal,
    onSaveFilesSuccessfully
}: IUploadFilesModal) => {
    const dispatch = useAppDispatch();
    const [firstFile, setFirstFile] = useState<any>();
    const [secondFile, setSecondFile] = useState<any>();
    const [response, setResponse] = useState<any>();
    const [generalError, setGeneralError] = useState<string>();
    const [submitLoading, setSubmitLoading] = useState<boolean>(false)

    const onDrop = useCallback((acceptedFiles: any) => {
        setFirstFile(acceptedFiles?.[0]);
    }, []);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
    });

    const { getRootProps: getRootPropsActive, getInputProps: getInputPropsActive } = useDropzone({
        onDrop: (acceptedFile) => {
            setSecondFile(acceptedFile[0]);
        },
    });

    const onSubmit = async () => {
        if (submitLoading) {
            return;
        }
        setSubmitLoading(true)
        setGeneralError(undefined)
        const formData = new FormData()

        if (firstFile) {
            formData.append('purchase_file', firstFile)
        }
        if (secondFile) {
            formData.append('invoice_file', secondFile)
        }
        try {
            const res = await dispatch(tryToUploadPurchaseInvoiceFileCompare(formData)).unwrap();
            setResponse(res)
            onSaveFilesSuccessfully()
            setGeneralError(undefined)
            setFirstFile(undefined)
            setSecondFile(undefined)
        } catch (error) {
            setGeneralError(`${error}`)
        }
        setSubmitLoading(false)
    };

    const onCloseModal = () => {
        setFirstFile(undefined)
        setSecondFile(undefined)
        setGeneralError(undefined)
        setResponse(undefined)
        setSubmitLoading(false)
        handleCloseModal()
    }

    return (
        <Modal
            open={openModal}
            showInRight={true}
            contentContainerStyle='!min-w-[100vw] md:!min-w-[650px]'
            onClose={onCloseModal}>
            <div className={'p-2 !min-w-[90vw] md:!min-w-[600px]'}>
                <p className='mt-2 mb-4 page-title !text-[18px] font-bold mr-10'>{response ? 'Results from Files' : 'Files to Compare'}</p>
                {response ?
                    <>
                        <div className='bg-[#e4ede657] rounded p-2 mb-2'>
                            {response?.length > 0 && response?.map((item: any, index: number) => {
                                const totalPurchasePrice = (item?.purchaseInfo?.items && item?.purchaseInfo?.items?.length > 0) && item?.purchaseInfo?.items?.reduce((accumulator: any, currentValue: any) => accumulator + (currentValue?.totalPrice), 0)
                                const findCurrency = item?.currency || item?.purchaseInfo?.currency
                                return (
                                    <div className='border-b border-b-[#E8F1FD] py-3 px-2'>
                                        <p className='mb-2'><span className='font-bold mr-2'>Purchase ID:</span>{item?.quoteId}</p>
                                        <p className='mb-2'><span className='font-bold mr-2'>Invoice Number:</span>{item?.invoiceNumber}</p>
                                        <p className='mb-2'><span className='font-bold mr-2'>Purchase Amount:</span>{CURRENCY_SYMBOLS[`${(findCurrency || '').toLowerCase()}`]}{Number(item?.analyzedData?.purchaseTotal || totalPurchasePrice || 0).toFixed(2)}</p>
                                        <p className='mb-2'><span className='font-bold mr-2'>Invoice Amount:</span>{CURRENCY_SYMBOLS[`${(findCurrency || '').toLowerCase()}`]}{Number(item?.analyzedData?.invoiceTotal || item?.invoiceInfo?.total || 0).toFixed(2)}</p>
                                        <p className='mb-2'><span className='font-bold mr-2'>Discrepancy:</span>{CURRENCY_SYMBOLS[`${(findCurrency || '').toLowerCase()}`]}{Number(item?.analyzedData?.discrepancy || 0).toFixed(2)}</p>
                                        {item?.analyzedData?.status && <p><span className='font-bold mr-2'>Status:</span>{(item?.analyzedData?.status || '').toLowerCase() === 'notmatched' ? 'Not Matched' : item?.analyzedData?.status}</p>}
                                    </div>
                                )
                            })}
                        </div>
                    </>
                    : <>
                        <div className='bg-[#e4ede657] rounded p-2 mb-2'>
                            <div>
                                <div
                                    className='flex flex-row items-center'
                                    {...getRootProps()}
                                >
                                    <input {...getInputProps()} />
                                    <Button
                                        icon={<img src='/assets/shared/add-plus.svg' className='w-[30px] object-contains pr-2' />}
                                        className={'btn-main !bg-white !py-2 !pr-3 !pl-2 !shadow-none !border-none flex flex-row items-center'}
                                        label={`Select Purchase Order File`}
                                        dataQa={'select-image'}
                                    />
                                    {(firstFile && firstFile?.name)
                                        ? <p className={'ml-3'}>{firstFile?.name}</p> : <></>
                                    }
                                </div>
                                <div
                                    className='flex flex-row items-center mt-5 mb-4'
                                    {...getRootPropsActive()}
                                >
                                    <input {...getInputPropsActive()} />
                                    <Button
                                        icon={<img src='/assets/shared/add-plus.svg' className='w-[30px] object-contains pr-2' />}
                                        className={'btn-main !bg-white !py-2 !pr-3 pl-2 !shadow-none !border-none flex flex-row items-center !min-w-[250px]'}
                                        label={'Select Invoice File'}
                                        dataQa={'select-transaction-file'}
                                    />
                                    {(secondFile && secondFile?.name)
                                        ? <p className={'ml-3'}>{secondFile?.name}</p> : <></>
                                    }
                                </div>
                            </div>
                        </div>
                        {generalError &&
                            <div className='flex flex-row justify-center my-3'>
                                <Error text={generalError} />
                            </div>
                        }
                        <div className='flex flex-row justify-end mt-4'>
                            <Button
                                label='Submit'
                                secondLoading={submitLoading}
                                className={submitLoading ? 'btn-primary-rounded-less-disable min-w-[150px]' : 'btn-primary-rounded-less min-w-[150px]'}
                                dataQa={'add-asset-save-btn'}
                                onClickButton={onSubmit}
                            />
                        </div>
                    </>
                }
            </div>
        </Modal>
    )
}
export default UploadFilesModal;
